import { updatePaginationState } from "@/store/paginationStore";
import { Button } from "@/components/ui/button";
import { RefreshCcwIcon } from "lucide-react";
import { PaginatedTariffsQueryVariables, TariffFilters } from "@/graphql/__generated__/graphql";
import { TariffsSortingDropdown, TariffsProvinceDropdown, TariffsActiveFromRange } from "./Components";
import { useRef } from "react";

interface Props {
  onFilterSubmit: (_variables: PaginatedTariffsQueryVariables) => void;
  displayAmount: number;
}

/**
 * TariffsFilter component handles filtering and sorting of tariffs
 * It manages filter state and provides UI for filter controls
 * @param props Component properties
 * @returns JSX element
 */
const TariffsFilter = (props: Props) => {
  const { onFilterSubmit, displayAmount } = props;

  // Ref to store the province dropdown reset function
  const provinceResetRef = useRef<(() => void) | null>(null);

  /**
   * Handles filter submission
   * @param newValues New filter values to submit
   */
  const handleSubmitClick = (newValues: PaginatedTariffsQueryVariables) => {
    onFilterSubmit(newValues);
  };

  /**
   * Resets all filter values and sort settings
   * Updates both local state and triggers query update
   */
  const handleResetClickFilters = () => {
    // Reset province dropdown local state
    if (provinceResetRef.current) {
      provinceResetRef.current();
    }

    // Define empty filters object
    const emptyFilters = {
      activeFrom: null,
      province: null,
    };

    // Reset all filter values and sort in the global store
    updatePaginationState({
      paginatedTariffs: {
        filters: emptyFilters as TariffFilters,
        sort: null
      }
    });

    // Create reset query variables with explicitly empty filters and sort
    const updatedVariables: PaginatedTariffsQueryVariables = {
      first: displayAmount,
      filters: {
        activeFrom: null,
        province: null,
      },
      sort: null
    };

    // Immediately submit the reset filter state to parent component
    onFilterSubmit(updatedVariables);
  };

  return (
    <div className="flex flex-1 gap-2">
      <div className="flex flex-1 items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Province Dropdown - Controls province filter */}
          <TariffsProvinceDropdown
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
            onReset={(resetFn) => {
              provinceResetRef.current = resetFn;
            }}
          />

          {/* Active From Range - Controls active from range filter */}
          <TariffsActiveFromRange
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Sorting Dropdown - Controls sort field and direction */}
          <TariffsSortingDropdown
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />
        </div>
        <div>
          <Button variant="outline" size="icon" onClick={handleResetClickFilters}>
            <RefreshCcwIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TariffsFilter;
